import { useState, useRef, useEffect, type FC } from 'react'
import { Info } from 'lucide-react'
import type { CardMeta } from './types'

const isDev = process.env.NODE_ENV === 'development'

type Props = {
  meta: CardMeta
}

const CardInfoButton: FC<Props> = ({ meta }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (
        btnRef.current?.contains(e.target as Node) ||
        panelRef.current?.contains(e.target as Node)
      ) return
      setOpen(false)
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [open])

  return (
    <span className='relative inline-flex'>
      <button
        ref={btnRef}
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='w-6 h-6 rounded-full flex items-center justify-center text-slate-500 bg-slate-50 border border-slate-200 hover:text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all'
        aria-label='Card info'
      >
        <Info className='w-4 h-4' />
      </button>

      {open && (
        <div
          ref={panelRef}
          className='absolute left-1/2 -translate-x-1/2 top-7 z-[1055] w-72 bg-white rounded-xl border border-slate-200 shadow-lg p-4 text-left'
        >
          <p className='text-[11px] text-slate-500 mb-2 leading-relaxed'>{meta.description}</p>
          {isDev && (
            <>
              <div className='mb-2'>
                <span className='text-[10px] font-semibold text-slate-400 uppercase tracking-wider'>Endpoint</span>
                <code className='block mt-0.5 text-[10px] bg-slate-50 text-slate-700 rounded px-1.5 py-1 break-all leading-relaxed'>{meta.endpoint}</code>
              </div>
              <div>
                <span className='text-[10px] font-semibold text-slate-400 uppercase tracking-wider'>Logic</span>
                <p className='mt-0.5 text-[11px] text-slate-600 leading-relaxed'>{meta.logic}</p>
              </div>
            </>
          )}
        </div>
      )}
    </span>
  )
}

export default CardInfoButton
